import { useState } from 'react';
import {
  IconSearch,
  IconUpload,
  IconFolderPlus,
  IconFolder,
  IconFileText,
  IconPhoto,
  IconVideo,
  IconFileZip,
  IconStar,
  IconStarFilled,
  IconDotsVertical,
  IconGridDots,
  IconList,
} from '@tabler/icons-react';
import { ActionIcon, Anchor, Box, Button, Card, Group, Select, Stack, Table, Text, TextInput, Title } from '@/components/ui';
// Breadcrumbs here uses the Mantine children API with per-item onClick (folder
// navigation); the @/components/ui Breadcrumbs wrapper is items/href-based and
// doesn't fit this dynamic case.
import { Breadcrumbs } from '@mantine/core';
import { mockFiles, getFilesByParent, formatFileSize, FileItem } from '../mockData/files';

const getFileIcon = (type: FileItem['type']) => {
  switch (type) {
    case 'folder': return IconFolder;
    case 'image': return IconPhoto;
    case 'video': return IconVideo;
    case 'archive': return IconFileZip;
    default: return IconFileText;
  }
};

const getFileColor = (type: FileItem['type']) => {
  switch (type) {
    case 'folder': return 'blue';
    case 'image': return 'green';
    case 'video': return 'red';
    case 'pdf': return 'red';
    case 'document': return 'blue';
    case 'spreadsheet': return 'green';
    case 'presentation': return 'orange';
    case 'archive': return 'gray';
    case 'code': return 'violet';
    default: return 'gray';
  }
};

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

export default function MyFilesPage() {
  const [search, setSearch] = useState('');
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [sortBy, setSortBy] = useState<string | null>('name');

  const currentFiles = getFilesByParent(currentFolder);

  const filteredFiles = currentFiles.filter(file =>
    file.name.toLowerCase().includes(search.toLowerCase())
  );

  // Sort files
  const sortedFiles = [...filteredFiles].sort((a, b) => {
    // Folders first
    if (a.type === 'folder' && b.type !== 'folder') return -1;
    if (a.type !== 'folder' && b.type === 'folder') return 1;

    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'modified':
        return b.modifiedAt.getTime() - a.modifiedAt.getTime();
      case 'size':
        return (b.size || 0) - (a.size || 0);
      default:
        return 0;
    }
  });

  // Build breadcrumb path
  const getBreadcrumbPath = () => {
    const paths: { id: string | null; name: string }[] = [{ id: null, name: 'My Files' }];
    let folderId = currentFolder;

    while (folderId) {
      const folder = mockFiles.find(f => f.id === folderId);
      if (folder) {
        paths.splice(1, 0, { id: folder.id, name: folder.name });
        folderId = folder.parentId;
      } else {
        break;
      }
    }

    return paths;
  };

  const handleFolderClick = (file: FileItem) => {
    if (file.type === 'folder') {
      setCurrentFolder(file.id);
    }
  };

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>My Files</Title>
          <Breadcrumbs mt="xs">
            {getBreadcrumbPath().map((item, index) => (
              <Anchor
                key={item.id || 'root'}
                size="sm"
                onClick={() => setCurrentFolder(item.id)}
                c={index === getBreadcrumbPath().length - 1 ? 'dimmed' : undefined}
              >
                {item.name}
              </Anchor>
            ))}
          </Breadcrumbs>
        </Box>
        <Group>
          <Button variant="light" leftSection={<IconFolderPlus size={16} />}>New Folder</Button>
          <Button leftSection={<IconUpload size={16} />}>Upload</Button>
        </Group>
      </Group>

      {/* Toolbar */}
      <Card>
        <Group justify="space-between">
          <Group>
            <TextInput
              placeholder="Search files..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              w={300}
            />
            <Select
              placeholder="Sort by"
              data={[
                { value: 'name', label: 'Name' },
                { value: 'modified', label: 'Modified' },
                { value: 'size', label: 'Size' },
              ]}
              value={sortBy}
              onChange={setSortBy}
              w={150}
            />
          </Group>
          <Group gap="xs">
            <ActionIcon
              variant={viewMode === 'grid' ? 'filled' : 'light'}
              onClick={() => setViewMode('grid')}
            >
              <IconGridDots size={16} />
            </ActionIcon>
            <ActionIcon
              variant={viewMode === 'list' ? 'filled' : 'light'}
              onClick={() => setViewMode('list')}
            >
              <IconList size={16} />
            </ActionIcon>
          </Group>
        </Group>
      </Card>

      {/* Files List/Grid */}
      <Card>
        {sortedFiles.length === 0 ? (
          <Box ta="center" py="xl">
            <IconFolder size={48} color="var(--mantine-color-dimmed)" />
            <Text size="sm" c="dimmed" mt="md">This folder is empty</Text>
          </Box>
        ) : viewMode === 'list' ? (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={40}></Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Modified</Table.Th>
                <Table.Th>Size</Table.Th>
                <Table.Th>Owner</Table.Th>
                <Table.Th w={80}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedFiles.map(file => {
                const Icon = getFileIcon(file.type);
                const color = getFileColor(file.type);
                return (
                  <Table.Tr
                    key={file.id}
                    style={{ cursor: file.type === 'folder' ? 'pointer' : 'default' }}
                    onClick={() => handleFolderClick(file)}
                  >
                    <Table.Td>
                      <ActionIcon variant="subtle" size="sm" onClick={(e) => e.stopPropagation()}>
                        {file.starred ? (
                          <IconStarFilled size={14} color="var(--mantine-color-yellow-filled)" />
                        ) : (
                          <IconStar size={14} />
                        )}
                      </ActionIcon>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="sm">
                        <Box
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 6,
                            backgroundColor: `var(--mantine-color-${color}-light)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Icon size={16} color={`var(--mantine-color-${color}-filled)`} />
                        </Box>
                        <Text size="sm">{file.name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{formatDate(file.modifiedAt)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{file.size ? formatFileSize(file.size) : '-'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{file.ownerName}</Text>
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon variant="subtle" size="sm" onClick={(e) => e.stopPropagation()}>
                        <IconDotsVertical size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        ) : (
          <Group gap="md">
            {sortedFiles.map(file => {
              const Icon = getFileIcon(file.type);
              const color = getFileColor(file.type);
              return (
                <Box
                  key={file.id}
                  p="md"
                  w={160}
                  style={{
                    backgroundColor: 'var(--mantine-color-gray-light)',
                    borderRadius: 8,
                    cursor: file.type === 'folder' ? 'pointer' : 'default',
                    textAlign: 'center',
                  }}
                  onClick={() => handleFolderClick(file)}
                >
                  <Box
                    mx="auto"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 8,
                      backgroundColor: `var(--mantine-color-${color}-light)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon size={24} color={`var(--mantine-color-${color}-filled)`} />
                  </Box>
                  <Text size="sm" fw={500} mt="sm" lineClamp={1}>{file.name}</Text>
                  <Text size="xs" c="dimmed">{file.size ? formatFileSize(file.size) : '-'}</Text>
                </Box>
              );
            })}
          </Group>
        )}
      </Card>
    </Stack>
  );
}
